import { AVAILABLE_TIMES } from '../constants';
import { BookingAvailabilitySlot, CapacityRule, SpecialOpening, VenueSchedule } from '../types';
import { formatDateOnly } from './dates';

export const DEFAULT_SLOT_INTERVAL_MINUTES = 30;
const MIN_SLOT_INTERVAL_MINUTES = 5;
const MAX_SLOT_INTERVAL_MINUTES = 180;
const DEFAULT_CONCURRENT_CAPACITY = 1;
const MIN_CONCURRENT_CAPACITY = 0;
const MAX_CONCURRENT_CAPACITY = 50;

export type DateBlockedResult = {
  blocked: boolean;
  reason?: string;
};

export const normalizeSlotIntervalMinutes = (value?: number) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_SLOT_INTERVAL_MINUTES;
  const rounded = Math.round(parsed / 5) * 5;
  return Math.min(MAX_SLOT_INTERVAL_MINUTES, Math.max(MIN_SLOT_INTERVAL_MINUTES, rounded));
};

const normalizeOptionalSlotIntervalMinutes = (value?: number | string | null) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return normalizeSlotIntervalMinutes(parsed);
};

export const parseTimeToMinutes = (time: string): number | null => {
  if (!/^\d{2}:\d{2}$/.test(time)) return null;
  const [hours, minutes] = time.split(':').map(Number);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

export const formatMinutesToTime = (minutes: number) => {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0');
  const m = (minutes % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
};

const resolveLocalDate = (value?: Date | string | null) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const text = String(value).trim();
  if (!text) return null;
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

export const isSlotInPast = (date: Date | string, time: string, now = new Date()) => {
  const localDate = resolveLocalDate(date);
  if (!localDate) return false;
  if (!isSameDay(localDate, now)) return false;
  const minutes = parseTimeToMinutes(time);
  if (minutes == null) return false;
  const slotDate = new Date(localDate);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  slotDate.setHours(hours, mins, 0, 0);
  return slotDate <= now;
};

export const filterPastSlotTimes = (
  date: Date | string | null | undefined,
  times: string[],
  now = new Date()
) => {
  const localDate = resolveLocalDate(date);
  if (!localDate) return times;
  if (!isSameDay(localDate, now)) return times;
  return times.filter((time) => !isSlotInPast(localDate, time, now));
};

export const filterPastAvailabilitySlots = (
  date: Date | string | null | undefined,
  slots: BookingAvailabilitySlot[],
  now = new Date()
) => {
  const localDate = resolveLocalDate(date);
  if (!localDate) return slots;
  if (!isSameDay(localDate, now)) return slots;
  return slots.filter((slot) => !isSlotInPast(localDate, slot.time, now));
};

const normalizeConcurrentCapacity = (value?: number, fallback = DEFAULT_CONCURRENT_CAPACITY) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(MAX_CONCURRENT_CAPACITY, Math.max(MIN_CONCURRENT_CAPACITY, Math.floor(parsed)));
};

const getSpecialOpeningForDate = (schedule: VenueSchedule | undefined, dateStr: string) => {
  const openings = Array.isArray(schedule?.specialOpenings) ? schedule?.specialOpenings : [];
  return openings.find((opening) => opening?.date === dateStr) || null;
};

const getDayDefaultCapacity = (
  schedule: VenueSchedule,
  dayOfWeek: number,
  specialOpening?: SpecialOpening | null
) => {
  if (specialOpening && specialOpening.capacity != null) {
    return normalizeConcurrentCapacity(specialOpening.capacity, DEFAULT_CONCURRENT_CAPACITY);
  }
  const dayConfig = schedule.weekly?.[dayOfWeek];
  if (!dayConfig) {
    return DEFAULT_CONCURRENT_CAPACITY;
  }
  return normalizeConcurrentCapacity(dayConfig.capacity, DEFAULT_CONCURRENT_CAPACITY);
};

const getCapacityRulesForDay = (schedule: VenueSchedule, dayOfWeek: number) => {
  const rules = Array.isArray(schedule.capacityRules) ? schedule.capacityRules : [];
  return rules
    .filter((rule): rule is CapacityRule => Number(rule?.dayOfWeek) === dayOfWeek)
    .map((rule) => ({
      start: parseTimeToMinutes(rule.start),
      end: parseTimeToMinutes(rule.end),
      capacity: normalizeConcurrentCapacity(rule.capacity),
      slotIntervalMinutes: normalizeOptionalSlotIntervalMinutes(rule.slotIntervalMinutes)
    }))
    .filter((rule) => rule.start != null && rule.end != null && rule.end > rule.start) as Array<{
    start: number;
    end: number;
    capacity: number;
    slotIntervalMinutes: number | null;
  }>;
};

const getEffectiveCapacityForInterval = (
  schedule: VenueSchedule,
  dayOfWeek: number,
  startMinute: number,
  endMinute: number,
  specialOpening?: SpecialOpening | null
) => {
  if (endMinute <= startMinute) return DEFAULT_CONCURRENT_CAPACITY;

  const rules = getCapacityRulesForDay(schedule, dayOfWeek);
  const defaultCapacity = getDayDefaultCapacity(schedule, dayOfWeek, specialOpening);
  if (rules.length === 0) return defaultCapacity;

  const boundaries = new Set<number>([startMinute, endMinute]);
  rules.forEach((rule) => {
    if (rule.start >= endMinute || rule.end <= startMinute) return;
    boundaries.add(Math.max(startMinute, rule.start));
    boundaries.add(Math.min(endMinute, rule.end));
  });

  const sortedBoundaries = [...boundaries].sort((a, b) => a - b);
  let minCapacity: number | null = null;

  for (let i = 0; i < sortedBoundaries.length - 1; i += 1) {
    const segmentStart = sortedBoundaries[i];
    const segmentEnd = sortedBoundaries[i + 1];
    if (segmentEnd <= segmentStart) continue;

    const matchingRules = rules.filter(
      (rule) => rule.start < segmentEnd && rule.end > segmentStart
    );
    const segmentCapacity =
      matchingRules.length > 0
        ? matchingRules.some((rule) => rule.capacity === 0)
          ? 0
          : Math.max(...matchingRules.map((rule) => rule.capacity))
        : defaultCapacity;

    minCapacity = minCapacity == null ? segmentCapacity : Math.min(minCapacity, segmentCapacity);
  }

  return minCapacity == null ? defaultCapacity : minCapacity;
};

const buildSlotStartsForDay = ({
  dayStart,
  dayEnd,
  requiredDurationMinutes,
  baseIntervalMinutes,
  capacityRules
}: {
  dayStart: number;
  dayEnd: number;
  requiredDurationMinutes: number;
  baseIntervalMinutes: number;
  capacityRules: Array<{
    start: number;
    end: number;
    slotIntervalMinutes: number | null;
  }>;
}) => {
  const durationMinutes = Math.max(0, Number(requiredDurationMinutes || 0));
  const effectiveDuration = durationMinutes > 0 ? durationMinutes : 1;
  const lastBookableStart = dayEnd - effectiveDuration;
  if (lastBookableStart < dayStart) return [];

  const slotStarts = new Set<number>();
  for (let cursor = dayStart; cursor <= lastBookableStart; cursor += baseIntervalMinutes) {
    slotStarts.add(cursor);
  }

  for (const rule of capacityRules) {
    if (!rule.slotIntervalMinutes) continue;

    const windowStart = Math.max(dayStart, rule.start);
    const windowEnd = Math.min(dayEnd, rule.end);
    if (windowEnd <= windowStart) continue;

    for (const slot of Array.from(slotStarts)) {
      if (slot >= windowStart && slot < windowEnd) {
        slotStarts.delete(slot);
      }
    }

    const interval = rule.slotIntervalMinutes;
    const lastRuleStart = Math.min(lastBookableStart, windowEnd - effectiveDuration);
    if (lastRuleStart < windowStart) continue;

    const offset = windowStart - rule.start;
    const firstSlot =
      offset <= 0 ? windowStart : rule.start + Math.ceil(offset / interval) * interval;
    for (let cursor = firstSlot; cursor <= lastRuleStart; cursor += interval) {
      slotStarts.add(cursor);
    }
  }

  return Array.from(slotStarts).sort((a, b) => a - b);
};

export const isDateBlockedBySchedule = (
  date: Date,
  schedule?: VenueSchedule,
  closedReason = 'Closed'
): DateBlockedResult => {
  if (!schedule) return { blocked: false };

  const dateStr = formatDateOnly(date);
  const dayOfWeek = date.getDay();
  const specialOpening = getSpecialOpeningForDate(schedule, dateStr);

  if (specialOpening) {
    return { blocked: false };
  }
  const dayConfig = schedule.weekly?.[dayOfWeek];

  if (dayConfig && !dayConfig.open) {
    return { blocked: true, reason: closedReason };
  }

  for (const rule of schedule.blockouts || []) {
    if (rule.type === 'single' && rule.date === dateStr) {
      return { blocked: true, reason: rule.reason };
    }

    if (rule.type === 'range' && rule.startDate && rule.endDate) {
      if (dateStr >= rule.startDate && dateStr <= rule.endDate) {
        return { blocked: true, reason: rule.reason };
      }
    }

    if (rule.type === 'pattern' && rule.dayOfWeek === dayOfWeek) {
      if (rule.frequency === 'every') {
        return { blocked: true, reason: rule.reason };
      }

      if (rule.frequency === 'biweekly' && rule.startFrom) {
        const start = new Date(rule.startFrom);
        const current = new Date(dateStr);
        const diffWeeks = Math.floor(
          Math.abs(current.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 7)
        );
        if (diffWeeks % 2 === 0) {
          return { blocked: true, reason: rule.reason };
        }
      }
    }
  }

  return { blocked: false };
};

export const getAvailableTimeSlotsForDate = (
  date: Date,
  schedule?: VenueSchedule,
  options?: {
    requiredDurationMinutes?: number;
    fallbackTimes?: string[];
  }
): string[] => {
  const fallbackTimes = options?.fallbackTimes || AVAILABLE_TIMES;
  if (!schedule) return fallbackTimes;

  const dateStr = formatDateOnly(date);
  const dayOfWeek = date.getDay();
  const specialOpening = getSpecialOpeningForDate(schedule, dateStr);
  const dayConfig = schedule.weekly?.[dayOfWeek];
  const scheduleStart = specialOpening?.start || dayConfig?.start;
  const scheduleEnd = specialOpening?.end || dayConfig?.end;
  if (!scheduleStart || !scheduleEnd) return [];
  if (!specialOpening && !dayConfig?.open) return [];

  const startMin = parseTimeToMinutes(scheduleStart);
  const endMin = parseTimeToMinutes(scheduleEnd);
  if (startMin == null || endMin == null || endMin <= startMin) return [];

  const requiredDuration = Math.max(0, Number(options?.requiredDurationMinutes || 0));
  const effectiveDuration = requiredDuration > 0 ? requiredDuration : 1;
  const interval = normalizeSlotIntervalMinutes(schedule.slotIntervalMinutes);
  const capacityRules = getCapacityRulesForDay(schedule, dayOfWeek);
  const slotStarts = buildSlotStartsForDay({
    dayStart: startMin,
    dayEnd: endMin,
    requiredDurationMinutes: requiredDuration,
    baseIntervalMinutes: interval,
    capacityRules
  });

  const slots: string[] = [];
  for (const cursor of slotStarts) {
    const capacity = getEffectiveCapacityForInterval(
      schedule,
      dayOfWeek,
      cursor,
      cursor + effectiveDuration,
      specialOpening
    );
    if (capacity <= 0) continue;
    slots.push(formatMinutesToTime(cursor));
  }
  return filterPastSlotTimes(date, slots);
};
